import React, { useRef, useState } from "react";
import Dialog from "../components/Dialog";
import { APPS_SCRIPT_URL } from "../config/constants";

const CurrentStudentsForm = ({ memberType }) => {
  const [formData, setFormData] = useState({});
  const [profilePicture, setProfilePicture] = useState(null);
  const [additionalDocument, setAdditionalDocument] = useState(null);
  const [documentType, setDocumentType] = useState("");
  const [dialog, setDialog] = useState(null);
  const fileInputRef = useRef(null);
  const documentInputRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleFileChange = (e, setFile, maxSize, allowedTypes, fileType) => {
    const file = e.target.files[0];
    if (file) {
      if (!allowedTypes.includes(file.type)) {
        setDialog({
          type: "error",
          message: `Invalid file type. Please upload a ${fileType} (${allowedTypes.join(
            ", "
          )}).`,
        });
        return;
      }
      if (file.size > maxSize * 1024 * 1024) {
        setDialog({
          type: "error",
          message: `File size exceeds ${maxSize}MB. Please upload a smaller ${fileType}.`,
        });
        return;
      }
      setFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!documentType) {
      setDialog({
        type: "error",
        message: "Please select a document type for your additional document.",
      });
      return;
    }
    try {
      const submissionData = new URLSearchParams({
        ...formData,
        memberType,
        documentType,
      });

      const filesToUpload = [
        { file: profilePicture, name: "profilePicture" },
        { file: additionalDocument, name: "additionalDocument" },
      ];

      for (const { file, name } of filesToUpload) {
        if (file) {
          const reader = new FileReader();
          reader.onload = async function (event) {
            const base64Data = event.target.result.split(",")[1];
            submissionData.append(name, base64Data);
            submissionData.append(`${name}Name`, file.name);
            submissionData.append(`${name}MimeType`, file.type);
          };
          await new Promise((resolve) => {
            reader.onloadend = resolve;
            reader.readAsDataURL(file);
          });
        }
      }

      await sendFormData(submissionData);
    } catch (err) {
      setDialog({
        type: "error",
        message: "An error occurred. Please try again.",
      });
    }
  };

  const sendFormData = async (formData) => {
    const response = await fetch(APPS_SCRIPT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: formData.toString(),
    });

    const data = await response.json();

    if (data.result === "success") {
      setDialog({
        type: "success",
        message: "Your registration has been submitted successfully!",
      });
      setFormData({});
      setProfilePicture(null);
      setAdditionalDocument(null);
      setDocumentType("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (documentInputRef.current) documentInputRef.current.value = "";
    } else {
      setDialog({
        type: "error",
        message: data.error || "Submission failed. Please try again.",
      });
    }
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 10 }, (_, i) => currentYear + i);

  const currentMemberFields = [
    { name: "name", label: "Name", type: "text" },
    { name: "email", label: "Email", type: "email" },
    { name: "mobileNumber", label: "Mobile Number", type: "tel" },
    { name: "whatsappNumber", label: "WhatsApp Number", type: "tel" },
    { name: "addressInIndia", label: "Address in India", type: "text" },
    { name: "state", label: "State", type: "text" },
    { name: "university", label: "Name of University", type: "text" },
    {
      name: "programme",
      label: "Programme",
      type: "select",
      options: [
        "Bachelor",
        "Masters",
        "PhD",
        "Diploma",
        "Fellowship",
        "Others",
      ],
    },
    { name: "course", label: "Course", type: "text" },
    {
      name: "courseDuration",
      label: "Course Duration (years)",
      type: "select",
      options: [1, 2, 3, 4, 5],
    },
    {
      name: "completionYear",
      label: "Year of Completion",
      type: "select",
      options: yearOptions,
    },
    {
      name: "funding",
      label: "Funding",
      type: "select",
      options: ["ICCR", "SII", "Gambia Government", "Private", "Other"],
    },
  ];

  return (
    <div>
      {dialog && (
        <Dialog
          type={dialog.type}
          message={dialog.message}
          onClose={() => setDialog(null)}
        />
      )}
      <form onSubmit={handleSubmit}>
        {currentMemberFields.map((field) => (
          <div key={field.name} className="mb-4">
            <label
              htmlFor={field.name}
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {field.label}
            </label>
            {field.type === "select" ? (
              <select
                id={field.name}
                name={field.name}
                value={formData[field.name] || ""}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select {field.label}</option>
                {field.options.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type={field.type}
                id={field.name}
                name={field.name}
                value={formData[field.name] || ""}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            )}
          </div>
        ))}
        <div className="mb-4">
          <label
            htmlFor="profilePicture"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Profile Picture (JPEG, PNG, GIF - Max 3MB)
          </label>
          <input
            type="file"
            id="profilePicture"
            name="profilePicture"
            accept="image/jpeg,image/png,image/gif"
            onChange={(e) =>
              handleFileChange(
                e,
                setProfilePicture,
                3,
                ["image/jpeg", "image/png", "image/gif"],
                "image"
              )
            }
            ref={fileInputRef}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="mb-4">
          <label
            htmlFor="documentType"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Select document type to be submit bellow
          </label>
          <select
            id="documentType"
            name="documentType"
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
            required
          >
            <option value="">Select Document Type</option>
            <option value="Bonafide">Bonafide Certificate</option>
            <option value="e-FRRO">e-FRRO</option>
            <option value="StudentID">Student ID</option>
          </select>
          <label
            htmlFor="additionalDocument"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Upload {documentType ? documentType : "Document"} (JPEG, PNG, GIF,
            PDF - Max 3MB)
          </label>
          <input
            type="file"
            id="additionalDocument"
            name="additionalDocument"
            accept="image/jpeg,image/png,image/gif,application/pdf"
            onChange={(e) =>
              handleFileChange(
                e,
                setAdditionalDocument,
                3,
                ["image/jpeg", "image/png", "image/gif", "application/pdf"],
                "document"
              )
            }
            ref={documentInputRef}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        <div className="mt-6">
          <button
            type="submit"
            className="w-full py-3 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 transition-colors duration-200"
          >
            Submit
          </button>
        </div>
      </form>
    </div>
  );
};

export default CurrentStudentsForm;
